import './style.scss';

import React from 'react';
import store from 'shared/utils/localStore';
import { emailCaptureEvent, checkEmailForAccount } from 'shared/models/api';
import { connect } from 'react-redux';
import {
  showLogin,
  closeLeadCapture,
  identify
} from 'shared/components/login/actions';
import { apply as addPromo } from 'components/promo/actions';
import { trackNow } from 'shared/models/analytic';
import { validEmail } from 'shared/models/user';
import config from 'sharedConfig';
import Modal from 'components/ui/modal';
import View from 'components/ui/view';
import Text from 'components/ui/text';
import Experiment from 'components/experiment';
import Variant from 'components/experiment/variant';

const PROMO_CODE = (config.promoCodes.signup.tenOff || '').toUpperCase();
const VIEWED_AT_KEY = 'scyLeadCaptureModalViewedAt';

class LeadCaptureModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showModal: false,
      over21: true,
      error: null,
      existingEmail: null,
      loading: false,
      success: false
    };

    this.close = this.close.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.showLogin = this.showLogin.bind(this);
  }

  componentDidMount() {
    this.timeoutId = this.showModal();
  }

  componentWillUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  showModal() {
    if (this.isLoggedIn()) return;
    if (this.hasSeenRecently()) return;

    const delay = 10 * 1000; // 10 seconds

    return setTimeout(() => {
      if (this.isLoggedIn()) return;
      if (this.hasSeenRecently()) return;
      if (
        this.isLoggingIn() ||
        this.isFillingInForm() ||
        this.hasOpenModal() ||
        this.isViewingCart()
      ) {
        this.timeoutId = this.showModal();
        return;
      }
      this.setState({ showModal: true });
      this.persistViewedAt();
      trackNow('Popup Offer - Email Form Viewed');
    }, delay);
  }

  isLoggedIn() {
    return this.props.login && !!this.props.login.user;
  }

  isLoggingIn() {
    return this.props.login && !!this.props.login.display;
  }

  isFillingInForm() {
    return !!document.querySelectorAll('input:focus').length;
  }

  isViewingCart() {
    return this.props.isViewingCart;
  }

  hasOpenModal() {
    return !!document.getElementsByClassName('modal-overlay').length;
  }

  hasSeenRecently() {
    const now = new Date();
    const lastSeen = store.get(VIEWED_AT_KEY);
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;
    return lastSeen > twentyFourHoursAgo;
  }

  persistViewedAt() {
    const now = new Date().valueOf();
    store.set(VIEWED_AT_KEY, now);
  }

  close(track = false) {
    this.setState({ showModal: false });
    this.props.closeLeadCapture();
    if (track) trackNow('Popup Offer - Closed');
  }

  showLogin(event) {
    event.preventDefault();
    this.close();
    this.props.showLogin('login');
    trackNow('Popup Offer - Show Login Clicked');
  }

  handleSubmit(e) {
    e.preventDefault();

    const { email, over21 } = this.state;

    this.setState({ error: null, existingEmail: null });

    if (!validEmail(email)) {
      this.setState({ error: 'Please enter a valid email address.' });
      return;
    }

    if (!over21) {
      this.setState({ error: 'You must be 21 or older to join Saucey.' });
      trackNow('Popup Offer - Under 21 Selected');
      return;
    }

    this.setState({ loading: true });
    return checkEmailForAccount(email).then(res => {
      this.setState({ loading: false });
      if (res && res.user) {
        this.setState({ existingEmail: email });
        trackNow('Popup Offer - Email Already Exists');
        return;
      } else {
        identify({ email });
        trackNow('Email Capture', {
          email,
          registrationSource: 'Email Capture'
        });
        emailCaptureEvent(email);
        this.setState({ success: true });
        trackNow('Popup Offer - Success Message Viewed');
      }
    });
  }

  render() {
    return (
      <VariantPicker>
        {({ variantClassName, inverse }) => (
          <Modal
            isOpen={this.state.showModal}
            handleClose={() => this.close(true)}
            className="lead-capture__modal"
            inverse={inverse}
          >
            <div
              className={`lead-capture__container row center-xs middle-xs start-md ${variantClassName}`}
            >
              <div className="col-xs-12 col-sm-7 col-md-12">
                {this.state.success ? (
                  <View padding={{ sides: 'sm' }} margin="xl">
                    <Text
                      title
                      size="xl"
                      weight="med"
                      tag="h1"
                      lg={{ size: 'xxl' }}
                      margin="none"
                    >
                      Welcome to the Good Life
                    </Text>
                    <View tag="p" margin="md">
                      Use code{' '}
                      <Text tag="span" weight="bold">
                        {PROMO_CODE} for $10 off your order
                      </Text>. We’ll email you our best deals, discounts and
                      special event invitations!
                    </View>
                    <div className="row">
                      <div className="col-xs-12 col-md-6">
                        <button
                          type="button"
                          className="btn-primary btn--full-width btn-block"
                          onClick={() => this.close()}
                        >
                          Start Shopping
                        </button>
                      </div>
                    </div>
                  </View>
                ) : (
                  <View padding={{ sides: 'sm' }} margin="md">
                    <View margin="sm">
                      <Text
                        title
                        size="xl"
                        weight="med"
                        tag="h1"
                        lg={{ size: 'xxl' }}
                        margin="none"
                      >
                        Get $10 Off!
                      </Text>
                      <Text tag="p" size="md">
                        Sign up to redeem a $10 discount on your first order.
                      </Text>
                    </View>
                    <form onSubmit={this.handleSubmit}>
                      <div className="row">
                        <div className="col-xs-12 col-md-8">
                          <View margin="xs">
                            <input
                              type="email"
                              name="email"
                              placeholder="Enter email address"
                              className="lead-capture__input"
                              value={this.state.email}
                              onChange={e =>
                                this.setState({ email: e.target.value })}
                            />
                          </View>
                        </div>
                        <div className="col-xs-12 col-md-4">
                          <button
                            type="submit"
                            className="btn-primary btn--full-width btn-block"
                            disabled={this.state.loading}
                          >
                            Sign Up
                          </button>
                        </div>
                      </div>
                      <View tag="p" margin="xs">
                        We promise{' '}
                        <Text tag="span" weight="bold">
                          never to spam
                        </Text>{' '}
                        or give your email away.
                      </View>
                      <label htmlFor="verify21" className="clickable">
                        <input
                          type="checkbox"
                          id="verify21"
                          name="verify21"
                          checked={this.state.over21}
                          onChange={e => {
                            this.setState({ over21: e.target.checked });
                          }}
                        />
                        <View inlineBlock padding={{ sides: 'xs' }} margin="sm">
                          I am at least 21 years of age.
                        </View>
                      </label>

                      <div style={{ height: '2em' }}>
                        {this.state.error && (
                          <Text error>{this.state.error}</Text>
                        )}
                        {this.state.existingEmail && (
                          <Text error>
                            We already have an account for{' '}
                            {this.state.existingEmail}.
                            <br />If that{"'"}s you,{' '}
                            <a
                              href="/?login=1"
                              className="link-text"
                              onClick={this.showLogin}
                            >
                              login
                            </a>.
                          </Text>
                        )}
                      </div>
                    </form>
                  </View>
                )}
              </div>
            </div>
          </Modal>
        )}
      </VariantPicker>
    );
  }
}

function VariantPicker({ children }) {
  return (
    <Experiment name="Email Capture BG" defaultVariant="Tux">
      <Variant name="Tux">
        {children({
          variantClassName: 'lead-capture__container--tux',
          inverse: false
        })}
      </Variant>
      <Variant name="Woman">
        {children({
          variantClassName: 'lead-capture__container--woman',
          inverse: true
        })}
      </Variant>
      <Variant name="Whiskey">
        {children({
          variantClassName: 'lead-capture__container--whiskey',
          inverse: false
        })}
      </Variant>
      <Variant name="Cocktail">
        {children({
          variantClassName: 'lead-capture__container--cocktail',
          inverse: true
        })}
      </Variant>
    </Experiment>
  );
}

function mapStateToProps(state) {
  return {
    login: state.login,
    isViewingCart: !!state.cart.show
  };
}

export default connect(mapStateToProps, {
  showLogin,
  addPromo,
  closeLeadCapture
})(LeadCaptureModal);



// WEBPACK FOOTER //
// ./shared/components/leadCaptureModal/index.js